import React, { useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Grid,
  Box,
  MenuItem,
  Divider,
  Alert,
  ListItemText,
} from '@mui/material';
import {
  formatCurrency,
  formatQueryError,
} from 'renderer/shared/formatters/formatters';
import { GetProductDetailsResponse } from 'renderer/features/products-page/schemas/products.schemas';
import _ from 'lodash';
import { platformOptions } from 'renderer/features/products-page/constants/productDropdowns';
import { Field, useFormikContext } from 'formik';
import { TextField as TextInput } from 'formik-mui';
import { dropdownKeyEventHandler } from 'renderer/shared/callbacks/keyEventHandler';
import productThumbnails from '../../products-page/constants/productThumbnails';
import { useGetPriceQuery } from '../services/purchase.endpoints';
import { PurchaseOptions } from '../schemas/purchase.schemas';
import HostAppsDropdown from './HostAppsDropdown';

const baseUrl = `${process.env.SERVER_URL ?? 'http://localhost:3000'}/api`;

interface OrderSummaryAdminProps {
  product?: GetProductDetailsResponse;
  disableCopies?: boolean;
  hidePricing?: boolean; // Add a new prop to control pricing display
  hidePlatform?: boolean; // Add a new prop to control platform display
}

const OrderSummaryAdmin: React.FC<OrderSummaryAdminProps> = ({
  product,
  disableCopies,
  hidePricing = false, // Default to false to show pricing in other components
  hidePlatform = false, // Default to false to show platform in other components
}) => {
  const { values, setFieldValue } = useFormikContext<PurchaseOptions>();

  const { data: prices, error } = useGetPriceQuery({
    productId: product?._id ?? '',
    discountCode: values.discountCode || undefined,
    copies: values.numberOfCopies || undefined,
    planId: values.selectedPlan || undefined,
  });

  // Ensure FCP is only selected with Macintosh
  useEffect(() => {
    if (!hidePlatform && values.hostApplication.id.includes('FxPlug')) {
      setFieldValue('platform', 'Macintosh');
    }
  }, [hidePlatform, setFieldValue, values.hostApplication.id]);

  // Ensure serial is required only for upgrades/crossgrades
  useEffect(() => {
    if (product && product.category !== 'Upgrade') {
      setFieldValue('serial', '-');
    }
  }, [product, product?.category, setFieldValue]);

  const supportedPlatforms = platformOptions.filter(
    ({ value }) =>
      !values.hostApplication.id.includes('FxPlug') || value === 'Macintosh',
  );

  return (
    <Card>
      <CardContent>
        <List>
          <ListItem>
            <ListItemAvatar>
              <Avatar
                variant="square"
                // src={productThumbnails.get(product?.productId ?? '') || ''}
                src={
                  product?.productImageId
                    ? `${baseUrl}/product-images/${product.productImageId}`
                    : '/images/placeholder.png'
                }
                alt={product?.name ?? 'Select a product'}
                sx={{ height: 100, width: 100, mr: 2 }}
              />
            </ListItemAvatar>
            <ListItemText
              primary={product?.name ?? 'Select a Product'}
              secondary={prices && formatCurrency(prices.startingPrice)}
            />
          </ListItem>
          <ListItem>
            {product?.category === 'Upgrade' && (
              <Field
                component={TextInput}
                fullWidth
                size="small"
                name="serial"
                label="Enter original serial number"
                inputProps={{ min: 1 }}
              />
            )}
          </ListItem>
          <ListItem>
            <HostAppsDropdown
              supportedHostApplications={
                product?.supportedHostApplications ?? []
              }
            />
          </ListItem>
          {!hidePlatform && (
            <ListItem>
              <Field
                component={TextInput}
                select
                onKeyUp={dropdownKeyEventHandler(supportedPlatforms, (option) =>
                  setFieldValue('platform', option),
                )}
                fullWidth
                size="small"
                label="Platform"
                labelId="platform"
                name="platform"
              >
                {supportedPlatforms.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Field>
            </ListItem>
          )}
          <ListItem>
            <Field
              component={TextInput}
              onBlur={() => {
                if (!_.isNumber(values.numberOfCopies)) {
                  setFieldValue('numberOfCopies', 1);
                }
              }}
              disabled={disableCopies}
              size="small"
              name="numberOfCopies"
              label="Number of Copies"
              type="number"
              inputProps={{ min: 1 }}
            />
          </ListItem>
        </List>
        {!hidePricing && ( // Conditionally render pricing
          <Box mt={2}>
            <Grid container>
              <Grid item xs={6}>
                <Typography>Subtotal</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography align="right">
                  {prices
                    ? formatCurrency(prices.subtotal)
                    : 'Fetching Price...'}
                </Typography>
              </Grid>
              {prices &&
                prices.discountDetails &&
                prices.discountDetails.success && (
                  <>
                    <Grid item xs={6}>
                      <Typography>Discount</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography align="right" color="green">
                        -{formatCurrency(prices.discountDetails.amount)}
                      </Typography>
                    </Grid>
                  </>
                )}
            </Grid>
            <Divider />
            <Grid container>
              <Grid item xs={6}>
                <Typography variant="subtitle1">Total</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle1" align="right">
                  {prices
                    ? formatCurrency(prices.totalPrice)
                    : 'Fetching Price...'}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
        {_.isNumber(values.numberOfCopies) && !!product && error && (
          <Alert severity="error">{formatQueryError(error)}</Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderSummaryAdmin;
